import { useRef, useMemo } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { Environment, Float, ContactShadows } from '@react-three/drei'
import * as THREE from 'three'

function Cube() {
  const meshRef = useRef<THREE.Mesh>(null)
  const materialRef = useRef<THREE.ShaderMaterial>(null)

  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uColor1: { value: new THREE.Color('#5ed29c') },
      uColor2: { value: new THREE.Color('#4fd8ff') },
      uColor3: { value: new THREE.Color('#8b5cf6') },
    }),
    []
  )

  const vertexShader = `
    varying vec2 vUv;
    varying vec3 vPosition;
    varying vec3 vNormal;
    uniform float uTime;
    
    void main() {
      vUv = uv;
      vPosition = position;
      vNormal = normal;
      
      vec3 pos = position;
      float distortion = sin(pos.x * 2.0 + uTime) * 0.1 + 
                       sin(pos.y * 2.0 + uTime * 0.8) * 0.1 +
                       sin(pos.z * 2.0 + uTime * 1.2) * 0.1;
      pos += normal * distortion;
      
      gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
    }
  `

  const fragmentShader = `
    uniform float uTime;
    uniform vec3 uColor1;
    uniform vec3 uColor2;
    uniform vec3 uColor3;
    
    varying vec2 vUv;
    varying vec3 vPosition;
    varying vec3 vNormal;
    
    void main() {
      float noise = sin(vPosition.x * 10.0 + uTime) * 
                    sin(vPosition.y * 10.0 + uTime * 0.7) * 
                    sin(vPosition.z * 10.0 + uTime * 1.3);
      
      vec3 color = mix(uColor1, uColor2, vUv.x + noise * 0.2);
      color = mix(color, uColor3, vUv.y + noise * 0.1);
      
      float fresnel = pow(1.0 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 2.0);
      color += fresnel * 0.3;
      
      gl_FragColor = vec4(color, 0.9);
    }
  `

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.x = state.clock.elapsedTime * 0.1
      meshRef.current.rotation.y = state.clock.elapsedTime * 0.15
    }
    if (materialRef.current) {
      materialRef.current.uniforms.uTime.value = state.clock.elapsedTime
    }
  })

  return (
    <Float speed={2} rotationIntensity={0.5} floatIntensity={0.5}>
      <mesh ref={meshRef} scale={2}>
        <boxGeometry args={[1, 1, 1, 32, 32, 32]} />
        <shaderMaterial
          ref={materialRef}
          vertexShader={vertexShader}
          fragmentShader={fragmentShader}
          uniforms={uniforms}
          transparent
        />
      </mesh>
    </Float>
  )
}

function FloatingCodeBlocks() {
  const groupRef = useRef<THREE.Group>(null)
  
  const codeSnippets = useMemo(() => {
    const snippets = []
    for (let i = 0; i < 8; i++) {
      snippets.push({
        position: [
          (Math.random() - 0.5) * 8,
          (Math.random() - 0.5) * 8,
          (Math.random() - 0.5) * 4 - 2,
        ] as [number, number, number],
        rotation: [Math.random() * Math.PI, Math.random() * Math.PI, 0] as [number, number, number],
        scale: 0.3 + Math.random() * 0.3,
      })
    }
    return snippets
  }, [])

  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.rotation.y = state.clock.elapsedTime * 0.05
    }
  })

  return (
    <group ref={groupRef}>
      {codeSnippets.map((snippet, i) => (
        <Float key={i} speed={1 + Math.random()} rotationIntensity={0.3} floatIntensity={0.3}>
          <mesh position={snippet.position} rotation={snippet.rotation} scale={snippet.scale}>
            <planeGeometry args={[2, 0.5]} />
            <meshBasicMaterial 
              color={i % 3 === 0 ? '#5ed29c' : i % 3 === 1 ? '#4fd8ff' : '#8b5cf6'} 
              transparent 
              opacity={0.3}
              side={THREE.DoubleSide}
            />
          </mesh>
        </Float>
      ))}
    </group>
  )
}

export function CodingCube() {
  return (
    <div className="w-full h-full">
      <Canvas camera={{ position: [0, 0, 6], fov: 45 }} dpr={[1, 2]}>
        <ambientLight intensity={0.5} />
        <pointLight position={[10, 10, 10]} intensity={1} />
        <pointLight position={[-10, -10, -10]} intensity={0.5} color="#5ed29c" />
        <Cube />
        <FloatingCodeBlocks />
        <ContactShadows position={[0, -3, 0]} opacity={0.3} scale={10} blur={2} />
        <Environment preset="city" />
      </Canvas>
    </div>
  )
}